import { useState, useCallback } from 'react'
import clsx from 'clsx'
import { Head, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import Button from '@/components/ui/Button'
import { FormSelect, FormInput, FormTextarea, FormMultiSelect } from '@/components/ui/form'
import { PlusCircleIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface NewOrderProps {
  companies: { id: number; name: string }[]
  customers: { id: number; name: string }[]
  vendors: { id: number; name: string }[]
  brands: { id: number; name: string }[]
  users: { id: number; name: string; email: string }[]
  tags: string[]
  currencies: [string, string][]
  seasons: [string, string][]
  order_types: [string, string][]
  parent_orders: { id: number; label: string }[]
}

// ─── Helpers ────────────────────────────────────

function isoDate(daysFromNow = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + daysFromNow)
  return d.toISOString().slice(0, 10)
}

// ─── Component ──────────────────────────────────

export default function NewOrder({
  companies, customers, vendors, brands, users, tags,
  currencies, seasons, order_types, parent_orders,
}: NewOrderProps) {
  // ─── Form state ─────────────────────────────
  const [form, setForm] = useState({
    order_type: 'parent_order',
    parent_id: '',
    company_id: '',
    user_id: '',
    customer: '',
    currency: 'usd',
    original_amount: '',
    comment: '',
    vendor: '',
    brand_owner: '',
    brands: [] as string[],
    tag_list: [] as string[],
    new_season: '',
    offer: '',
    order_date: isoDate(),
    customer_po: '',
    po_date: isoDate(),
    po_start_shipping: isoDate(30),
    cancel_date: isoDate(60),
  })
  const [productsFile, setProductsFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({})
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)

  const set = useCallback(<K extends keyof typeof form>(key: K, val: (typeof form)[K]) => {
    setForm(prev => ({ ...prev, [key]: val }))
  }, [])

  // ─── CSRF ───────────────────────────────────
  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  // ─── Validation ─────────────────────────────
  const validate = (): string[] => {
    const errs: string[] = []
    const fe: Record<string, boolean> = {}
    const req = (field: string, label: string, value: unknown) => {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        errs.push(`${label} is required`)
        fe[field] = true
      }
    }
    req('company_id', 'Company', form.company_id)
    req('user_id', 'Sales Rep', form.user_id)
    req('customer', 'Customer', form.customer)
    req('vendor', 'Vendor', form.vendor)
    req('new_season', 'Season', form.new_season)
    req('order_date', 'Order Date', form.order_date)
    req('po_start_shipping', 'Start Ship Date', form.po_start_shipping)
    req('cancel_date', 'Cancel Date', form.cancel_date)
    req('brands', 'At least one Brand', form.brands)
    req('tag_list', 'At least one Category', form.tag_list)
    if (form.order_type === 'child_order' && !form.parent_id) {
      errs.push('Parent Order is required for child orders')
      fe['parent_id'] = true
    }
    setFieldErrors(fe)
    return errs
  }

  // ─── Submit ─────────────────────────────────
  const handleSubmit = async () => {
    const validationErrors = validate()
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }
    setErrors([])
    setFieldErrors({})
    setSubmitting(true)

    try {
      const fd = new FormData()
      const prefix = 'new_customer_order_form'
      // Scalar fields
      const scalars = [
        'order_type', 'parent_id', 'company_id', 'user_id', 'customer', 'currency',
        'original_amount', 'comment', 'vendor', 'brand_owner', 'new_season', 'offer',
        'order_date', 'customer_po', 'po_date', 'po_start_shipping', 'cancel_date',
      ] as const
      for (const key of scalars) {
        fd.append(`${prefix}[${key}]`, form[key])
      }
      // Array fields
      for (const b of form.brands) fd.append(`${prefix}[brands][]`, b)
      for (const t of form.tag_list) fd.append(`${prefix}[tag_list][]`, t)
      // File
      if (productsFile) fd.append(`${prefix}[products_file]`, productsFile)

      const res = await fetch('/inertia_customer_orders', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: fd,
      })

      if (res.ok) {
        const data = await res.json()
        router.visit(data.redirect_url)
      } else {
        const data = await res.json().catch(() => ({ errors: ['Server error'] }))
        setErrors(data.errors || ['An error occurred'])
      }
    } catch {
      setErrors(['Network error'])
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Options ────────────────────────────────
  const companyOptions = companies.map(c => ({ value: String(c.id), label: c.name }))
  const userOptions = users.map(u => ({ value: String(u.id), label: u.name || u.email }))
  const customerOptions = customers.map(c => ({ value: c.name, label: c.name }))
  const vendorOptions = vendors.map(v => ({ value: v.name, label: v.name }))
  const brandOptions = brands.map(b => ({ value: b.name, label: b.name }))
  const tagOptions = tags.map(t => ({ value: t, label: t }))
  const currencyOptions = currencies.map(([val, label]) => ({ value: val, label }))
  const seasonOptions = seasons.map(([val, label]) => ({ value: val, label }))
  const orderTypeOptions = order_types.map(([val, label]) => ({ value: val, label }))
  const parentOrderOptions = parent_orders.map(p => ({ value: String(p.id), label: p.label }))

  // ─── Render ─────────────────────────────────
  return (
    <>
      <Head title="Create New Order" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      <PageHeader title="Create New Order" subtitle="Customer Orders" icon={<PlusCircleIcon className="h-5 w-5" />} />

      {errors.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20" data-testid="form-errors">
          <ul className="list-disc pl-4 text-sm text-red-700 dark:text-red-400">
            {errors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* ─── Column 1: Order Info ──────────── */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Order Info</h3>

          <Field label="Order Type *">
            <FormSelect value={form.order_type} onChange={v => set('order_type', v)} options={orderTypeOptions} />
          </Field>

          {form.order_type === 'child_order' && (
            <Field label="Parent Order *" error={fieldErrors.parent_id}>
              <FormSelect value={form.parent_id} onChange={v => set('parent_id', v)} options={parentOrderOptions} placeholder="Select parent..." />
            </Field>
          )}

          <Field label="Company *" error={fieldErrors.company_id}>
            <FormSelect value={form.company_id} onChange={v => set('company_id', v)} options={companyOptions} placeholder="Select company..." />
          </Field>

          <Field label="Sales Rep *" error={fieldErrors.user_id}>
            <FormSelect value={form.user_id} onChange={v => set('user_id', v)} options={userOptions} placeholder="Select sales rep..." />
          </Field>

          <Field label="Customer *" error={fieldErrors.customer}>
            <FormSelect value={form.customer} onChange={v => set('customer', v)} options={customerOptions} placeholder="Select customer..." />
          </Field>

          <Field label="Currency">
            <FormSelect value={form.currency} onChange={v => set('currency', v)} options={currencyOptions} />
          </Field>

          <Field label="Original Amount">
            <FormInput type="number" value={form.original_amount} onChange={e => set('original_amount', (e.target as HTMLInputElement).value)} placeholder="0.00" />
          </Field>

          <Field label="Comments">
            <FormTextarea value={form.comment} onChange={e => set('comment', (e.target as HTMLTextAreaElement).value)} rows={3} placeholder="Order comments..." />
          </Field>
        </div>

        {/* ─── Column 2: Product Info ──────────── */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Product Info</h3>

          <Field label="Vendor *" error={fieldErrors.vendor}>
            <FormSelect value={form.vendor} onChange={v => set('vendor', v)} options={vendorOptions} placeholder="Select vendor..." />
          </Field>

          <Field label="Brand Owner">
            <FormInput value={form.brand_owner} onChange={e => set('brand_owner', (e.target as HTMLInputElement).value)} placeholder="Brand owner..." />
          </Field>

          <Field label="Brands *" error={fieldErrors.brands}>
            <FormMultiSelect values={form.brands} options={brandOptions} onChange={v => set('brands', v as string[])} placeholder="Search brands..." />
          </Field>

          <Field label="Categories *" error={fieldErrors.tag_list}>
            <FormMultiSelect values={form.tag_list} options={tagOptions} onChange={v => set('tag_list', v as string[])} allowCreate placeholder="Search categories..." />
          </Field>

          <Field label="Season *" error={fieldErrors.new_season}>
            <FormSelect value={form.new_season} onChange={v => set('new_season', v)} options={seasonOptions} placeholder="Select season..." />
          </Field>

          <Field label="Offer #">
            <FormInput value={form.offer} onChange={e => set('offer', (e.target as HTMLInputElement).value)} placeholder="Offer number..." />
          </Field>
        </div>

        {/* ─── Column 3: Dates ──────────── */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Dates</h3>

          <Field label="Order Date *" error={fieldErrors.order_date}>
            <FormInput type="date" value={form.order_date} onChange={e => set('order_date', (e.target as HTMLInputElement).value)} />
          </Field>

          <Field label="Customer PO #">
            <FormInput value={form.customer_po} onChange={e => set('customer_po', (e.target as HTMLInputElement).value)} placeholder="PO number..." />
          </Field>

          <Field label="PO Date">
            <FormInput type="date" value={form.po_date} onChange={e => set('po_date', (e.target as HTMLInputElement).value)} />
          </Field>

          <Field label="Start Ship Date *" error={fieldErrors.po_start_shipping}>
            <FormInput type="date" value={form.po_start_shipping} onChange={e => set('po_start_shipping', (e.target as HTMLInputElement).value)} />
          </Field>

          <Field label="Cancel Date *" error={fieldErrors.cancel_date}>
            <FormInput type="date" value={form.cancel_date} onChange={e => set('cancel_date', (e.target as HTMLInputElement).value)} />
          </Field>
        </div>
      </div>

      {/* ─── Bottom: File upload + Submit ──────────── */}
      <div className="mt-6 flex items-end justify-between border-t border-zinc-200 pt-4 dark:border-zinc-700">
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Products Spreadsheet
          </label>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={e => setProductsFile(e.target.files?.[0] || null)}
            className="text-sm text-zinc-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100 dark:text-zinc-400 dark:file:bg-indigo-500/10 dark:file:text-indigo-400"
          />
          {productsFile && (
            <p className="mt-1 text-xs text-zinc-500">{productsFile.name}</p>
          )}
        </div>

        <Button disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Creating...' : 'Create Order'}
        </Button>
      </div>
    </>
  )
}

// ─── Field wrapper ──────────────────────────────

function Field({ label, error, children }: { label: string; error?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className={clsx('mb-1 block text-xs font-medium', error ? 'text-red-600 dark:text-red-400' : 'text-zinc-700 dark:text-zinc-300')}>{label}</label>
      <div className={error ? '[&_[data-slot=control]]:ring-2 [&_[data-slot=control]]:ring-red-500 [&_[data-slot=control]]:rounded-lg' : ''}>
        {children}
      </div>
    </div>
  )
}

NewOrder.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
